import { useEffect, useImperativeHandle, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import { createFundWatchData } from "@hsl/fc-subscription/lib";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import type {
    SelectDocumentsStep,
    UserInput,
} from "@hsl/fc-subscription/types";

import useFirstFocus from "../../../../../hooks/useFirstFocus";
import useTranslation, { Translate } from "../../../../../hooks/useTranslation";
import { italianTitlesToEnglish } from "../../../../../lib/italianTitles";
import {
    ApiError,
    UserDetailsService,
    type UserDetails,
} from "../../../../../openapi";
import Briefcase from "../../../../icons/Briefcase";
import EnvelopeOpen from "../../../../icons/EnvelopeOpen";
import Globe from "../../../../icons/Globe";
import UserIcon from "../../../../icons/User";
import Select from "../../../../Select";
import { createFCSError } from "../../../lib";
import { useSubscriptionContext } from "../../../Root";
import { Card } from "../../Card";
import { DetailFormInput, InvalidInput } from "./FormComponents";
import { getProfiles } from "./services";

interface Props {
    titleNullValue?: string;
    profileNullValue?: string;
    controlRef?: React.RefObject<{ submit: () => Promise<void> }>;
}

export default function AddDetails({
    titleNullValue = "Select",
    profileNullValue = "Select profile",
    controlRef,
}: Props) {
    const tx = useTranslation();
    const { inputFieldEvent, pageLoadEvent } = useYardActionEvents();
    const handleFocus = useFirstFocus<string>((name) =>
        inputFieldEvent({
            name,
        }),
    );
    const getStepData = useFCSubscriptionStore(
        (state) => state.getFlowStepData,
    );
    const setStepData = useFCSubscriptionStore(
        (state) => state.setFlowStepData,
    );
    const setPending = useFCSubscriptionStore(
        (state) => state.setPendingSubscriptions,
    );
    const clearBasket = useFCSubscriptionStore((state) => state.clearBasket);
    const basketMode = useFCSubscriptionStore(
        (state) => state.selectionBasketMode,
    );
    const sharingMode = useFCSubscriptionStore((state) => state.sharingMode);

    const {
        audienceMeta: { userTitles },
        config: {
            audienceId,
            locale,
            audienceShortName,
            jurisdictionName,
            jurisdictionCode,
        },
    } = useSubscriptionContext();

    const subscriptions =
        basketMode === "subscribe"
            ? getStepData("select-documents")
            : getStepData("select-funds")?.data?.funds.reduce(
                  (acc, fund) => {
                      const subscriptions = acc.data?.subscriptions ?? [];
                      return {
                          ...acc,
                          data: {
                              subscriptions: [
                                  ...subscriptions,
                                  createFundWatchData(fund),
                              ],
                          },
                      };
                  },
                  { data: [{ subscriptions: [] }] } as Pick<
                      SelectDocumentsStep,
                      "data"
                  >,
              );

    const stepData = getStepData("add-details");

    const retrieve = useForm<Pick<UserInput, "email">>(
        stepData?.data?.mode === "retrieve"
            ? { defaultValues: stepData.data.data }
            : {},
    );

    const register = useForm<UserInput>(
        stepData?.data?.mode === "register"
            ? { defaultValues: stepData.data.data }
            : {},
    );

    const [activeForm, setActiveForm] = useState<
        "retrieve" | "register" | null
    >(null);

    const titleOptions = [titleNullValue, ...userTitles].map((t) => ({
        key: t,
        label: t === titleNullValue ? `- ${tx(t).result} -` : tx(t).result,
        value: t,
    }));
    const { data: profileOptions } = useQuery({
        queryKey: ["profiles", audienceId, profileNullValue],
        queryFn: async () => getProfiles(audienceId, profileNullValue),
        cacheTime: Infinity,
        refetchInterval: Infinity,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        const watch = retrieve.watch((values) => {
            const isUsingForm = Object.values(values).some((v) => !!v);
            setActiveForm(isUsingForm ? "retrieve" : null);
        });
        return () => watch.unsubscribe();
    }, [retrieve]);

    useEffect(() => {
        const watch = register.watch((values) => {
            const isUsingForm = Object.values(values).some((v) => !!v);
            setActiveForm(isUsingForm ? "register" : null);
        });
        return () => watch.unsubscribe();
    }, [register]);

    const retrieveAccount = useMutation({
        mutationFn: async ({ email }: Pick<UserInput, "email">) => {
            try {
                await UserDetailsService.resendUserEmail({ email });
                if (subscriptions?.data) {
                    const { subscriptions: subs = [], watchlist = [] } =
                        subscriptions.data;
                    setPending([...subs, ...watchlist]);
                }
            } catch (err) {
                throw new Error(
                    tx(
                        "Could not retrieve your funds. Please try again",
                    ).result,
                );
            }
        },
        onSuccess: () => {
            clearBasket();
            setStepData("add-details", {
                complete: true,
                data: {
                    mode: "retrieve",
                    data: retrieve.getValues(),
                },
            });
        },
    });

    const registerAccount = useMutation({
        mutationFn: async (values: UserInput) => {
            try {
                const { subscriptions: subs = [], watchlist = [] } =
                    subscriptions?.data ?? {};

                return await UserDetailsService.registerUser({
                    subscriptions:
                        sharingMode === "none" ? [...subs, ...watchlist] : [],
                    userDetails: {
                        ...values,
                        title:
                            locale === "it_IT"
                                ? italianTitlesToEnglish(values.title)
                                : values.title,
                        profile: values.profile as UserInput["profile"],
                        language: locale as UserDetails["language"],
                        dayOfWeek: subscriptions?.data?.dayOfWeek ?? "MONDAY",
                        country: jurisdictionCode as UserDetails["country"],
                        audience: audienceShortName,
                    },
                });
            } catch (err) {
                if (err instanceof ApiError) {
                    throw createFCSError(err.status, tx);
                }
            }
        },
        onSuccess: () => {
            clearBasket();
            setStepData("add-details", {
                complete: true,
                data: {
                    mode: "register",
                    data: register.getValues(),
                },
            });
        },
    });

    useImperativeHandle(
        controlRef,
        () => ({
            submit: async () => {
                switch (activeForm) {
                    case "retrieve": {
                        await retrieve.handleSubmit(
                            (data) => retrieveAccount.mutateAsync(data),
                            (errors) => {
                                const firstError = Object.entries(errors)[0];
                                if (firstError) {
                                    retrieve.setError(
                                        // @ts-expect-error - seterror is too tightly typed
                                        firstError[0],
                                        firstError[1],
                                    );
                                }
                                throw new Error(
                                    tx(
                                        "Error in form - please fix before submitting",
                                    ).result,
                                );
                            },
                        )();
                        break;
                    }
                    case "register": {
                        await register.handleSubmit(
                            (data) => registerAccount.mutateAsync(data),
                            (errors) => {
                                const firstError = Object.entries(errors)[0];
                                if (firstError) {
                                    register.setError(
                                        // @ts-expect-error - seterror is too tightly typed
                                        firstError[0],
                                        firstError[1],
                                    );
                                }
                                throw new Error(
                                    tx(
                                        "Error in form - please fix before submitting",
                                    ).result,
                                );
                            },
                        )();
                        break;
                    }
                    default:
                        throw new Error(
                            tx("Please fill out one of the forms").result,
                        );
                }
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            activeForm,
            register.handleSubmit,
            registerAccount.mutateAsync,
            retrieve.handleSubmit,
            retrieveAccount.mutateAsync,
        ],
    );

    useEffect(() => {
        setTimeout(() => pageLoadEvent({ pageName: "Add your details" }), 50);
    }, []);

    return (
        <Card className="space-y-8">
            <div
                className={cx(
                    "transition-opacity",
                    activeForm === "retrieve" && "opacity-50",
                )}
            >
                <h2 className="my-2 text-lg font-medium">
                    <Translate>Enter your details</Translate>
                </h2>
                <p className="my-2">
                    <Translate>
                        If you haven’t previously set up a subscription or
                        watchlist, please provide your details below. We will
                        then send you an email with a verification link.
                    </Translate>
                </p>
                <form className="mt-4 grid grid-cols-1 gap-8 sm:grid-cols-2">
                    <div className="flex flex-col gap-8">
                        <DetailFormInput icon={<UserIcon />}>
                            <Controller
                                name="title"
                                control={register.control}
                                disabled={activeForm === "retrieve"}
                                rules={{
                                    required: tx("Please select title").result,
                                }}
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        options={titleOptions}
                                        onChange={(value: string) => {
                                            const v =
                                                value === titleNullValue
                                                    ? ""
                                                    : value;
                                            register.setValue("title", v);
                                        }}
                                        onFocus={() =>
                                            handleFocus("title", "Title")
                                        }
                                        selectedClassName="text-base"
                                        optionClassName="text-base"
                                        buttonClassName="px-4 py-2.5 border rounded-sm border-taupe-grey"
                                        className="max-sm:w-full"
                                    />
                                )}
                            />
                            <InvalidInput
                                error={register.formState.errors.title}
                            />
                            <input
                                type="text"
                                autoComplete="given-name"
                                placeholder={tx("First name").result}
                                onFocus={() =>
                                    handleFocus("fname", "First name")
                                }
                                {...register.register("firstName", {
                                    required: tx("Please enter first name")
                                        .result,
                                    disabled: activeForm === "retrieve",
                                    maxLength: {
                                        value: 40,
                                        message: tx(
                                            "First name cannot be longer than 40 characters",
                                        ).result,
                                    },
                                })}
                            />
                            <InvalidInput
                                error={register.formState.errors.firstName}
                            />
                            <input
                                type="text"
                                autoComplete="family-name"
                                placeholder={tx("Last name").result}
                                onFocus={() =>
                                    handleFocus("lname", "Last name")
                                }
                                {...register.register("lastName", {
                                    required: tx("Please enter last name")
                                        .result,
                                    disabled: activeForm === "retrieve",
                                    maxLength: {
                                        value: 80,
                                        message: tx(
                                            "Last name cannot be longer than 80 characters",
                                        ).result,
                                    },
                                })}
                            />
                            <InvalidInput
                                error={register.formState.errors.lastName}
                            />
                        </DetailFormInput>
                        <DetailFormInput icon={<EnvelopeOpen />}>
                            <input
                                type="email"
                                autoComplete="email"
                                placeholder={tx("Email").result}
                                onFocus={() => handleFocus("email-2", "Email")}
                                {...register.register("email", {
                                    required: tx("Please enter email address")
                                        .result,
                                    disabled: activeForm === "retrieve",
                                    pattern: {
                                        value: /\S+@\S+\.\S+/,
                                        message: tx(
                                            "Please enter a valid email address",
                                        ).result,
                                    },
                                })}
                            />
                            <InvalidInput
                                error={register.formState.errors.email}
                            />
                        </DetailFormInput>
                    </div>
                    <div className="flex flex-col gap-8">
                        <DetailFormInput icon={<Briefcase />}>
                            <input
                                type="text"
                                autoComplete="organization"
                                placeholder={tx("Company").result}
                                onFocus={() =>
                                    handleFocus("company", "Company")
                                }
                                {...register.register("company", {
                                    required: tx("Please enter company").result,
                                    disabled: activeForm === "retrieve",
                                    maxLength: {
                                        value: 255,
                                        message: tx(
                                            "Company cannot be longer than 255 characters",
                                        ).result,
                                    },
                                })}
                            />
                            <InvalidInput
                                error={register.formState.errors.company}
                            />
                            <input
                                type="text"
                                autoComplete="job-title"
                                placeholder={tx("Job title").result}
                                onFocus={() =>
                                    handleFocus("jTitle", "Job Title")
                                }
                                {...register.register("jobTitle", {
                                    required: tx("Please enter job title")
                                        .result,
                                    disabled: activeForm === "retrieve",
                                    maxLength: {
                                        value: 128,
                                        message: tx(
                                            "Job title cannot be longer than 128 characters",
                                        ).result,
                                    },
                                })}
                            />
                            <InvalidInput
                                error={register.formState.errors.jobTitle}
                            />
                        </DetailFormInput>
                        {profileOptions && (
                            <DetailFormInput icon={<Globe />}>
                                <div className="flex w-full flex-col gap-4">
                                    <p className="mt-3">
                                        <Translate>
                                            {jurisdictionName}
                                        </Translate>
                                    </p>
                                    <Controller
                                        name="profile"
                                        control={register.control}
                                        disabled={activeForm === "retrieve"}
                                        rules={{
                                            required: tx(
                                                "Please select profile",
                                            ).result,
                                        }}
                                        render={({ field }) => (
                                            <Select
                                                value={field.value}
                                                options={profileOptions}
                                                onChange={(value: string) => {
                                                    const v =
                                                        value ===
                                                        profileNullValue
                                                            ? ""
                                                            : value;
                                                    register.setValue(
                                                        "profile",
                                                        v as UserInput["profile"],
                                                    );
                                                }}
                                                onFocus={() =>
                                                    handleFocus(
                                                        "profile",
                                                        "Profile",
                                                    )
                                                }
                                                selectedClassName="text-base"
                                                optionClassName="text-base"
                                                buttonClassName="px-4 py-2.5 border rounded-sm border-taupe-grey max-sm:max-w-[90%]"
                                                className="z-[100]"
                                                scrollerClassName="max-sm:max-w-full"
                                            />
                                        )}
                                    />
                                    <InvalidInput
                                        error={
                                            register.formState.errors.profile
                                        }
                                    />
                                </div>
                            </DetailFormInput>
                        )}
                    </div>
                </form>
            </div>
            <div
                className={cx(
                    "flex w-full items-center gap-2 font-medium transition-opacity [&_hr]:flex-1",
                    activeForm !== null && "opacity-50",
                )}
            >
                <hr />
                <p>
                    <Translate>Or</Translate>
                </p>
                <hr />
            </div>
            <div
                className={cx(
                    "transition-opacity",
                    activeForm === "register" && "opacity-50",
                )}
            >
                <h2 className="mb-2 text-lg font-medium">
                    <Translate>Existing user</Translate>
                </h2>
                <p className="my-2">
                    <Translate>
                        If you previously set up a subscription or watchlist,
                        please provide your email address. If you have
                        subscriptions or a watchlist, we will then send you an
                        email with a verification link.
                    </Translate>
                </p>
                <form>
                    <DetailFormInput icon={<EnvelopeOpen />} className="mt-4">
                        <input
                            type="email"
                            autoComplete="email"
                            placeholder={tx("Email").result}
                            disabled={activeForm === "register"}
                            onFocus={() => handleFocus("email", "Email")}
                            {...retrieve.register("email", {
                                required: tx("Please enter email address")
                                    .result,
                                pattern: {
                                    value: /\S+@\S+\.\S+/,
                                    message: tx(
                                        "Please enter a valid email address",
                                    ).result,
                                },
                            })}
                        />
                        <InvalidInput error={retrieve.formState.errors.email} />
                    </DetailFormInput>
                </form>
            </div>
        </Card>
    );
}
